import Header from './Header';
import LeftRail from './LeftRail';
import TemplateRail from './TemplateRail';
import IssueSidebar from '../IssueSidebar';
import DictationPanel from '../DictationPanel';
import ReportEditor from '../ReportEditor';
import DebugView from '../ReportEditor/DebugView';
import ModelComparison from '../ReportEditor/ModelComparison';
import ExportPanel from '../ExportPanel';
import FeedbackButton from '../Feedback/FeedbackButton';
import { useEffect } from 'react';
import { useReportStore } from '../../stores/reportStore';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { Bug, Columns, FileText } from 'lucide-react';

// Debug and Compare are development views — switched off for production use
// (2026-07-20). Flip to true to bring them back for testing; all wiring
// (components, centerView state, tab recipe) is kept intact.
const SHOW_DEV_VIEWS = false;

// Issues pane (dictation gaps + validation) — switched off 2026-07-21 after
// radiologist feedback that it was too obstructive mid-workflow. All wiring
// (IssueSidebar, store flag, gap detector, rail toggle) is kept; flip to
// true when we redesign it with him.
const SHOW_ISSUES_PANEL = false;

export default function MainLayout() {
  const { centerView, setCenterView, setEditorTab, templatesPanelOpen, setTemplatesPanelOpen,
    issuesPanelOpen, setIssuesPanelOpen, issues, transcription, editedReport } = useReportStore();
  const { preferences } = usePreferencesStore();

  const isDark = preferences?.ui_theme === 'dark';

  // Warn before an accidental refresh/tab-close discards an unsaved dictation
  // or edited report. reportStore is intentionally NOT persisted, so a reload
  // loses everything — losing dictations is the #1 historical complaint. The
  // guard only attaches when there is actual work to lose.
  useEffect(() => {
    const hasUnsavedWork = transcription.trim() !== '' || editedReport.trim() !== '';
    if (!hasUnsavedWork) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [transcription, editedReport]);

  return (
    <div className={`min-h-screen ${isDark ? 'dark' : ''}`}>
      <div className="min-h-screen bg-gray-50 dark:bg-surface-950 flex flex-col">
        <Header />

        {/* RAAS-style shell: icon rail | dictation + editor | docked template panel */}
        <div className="flex flex-1 overflow-hidden">
          <LeftRail
            showTemplates={templatesPanelOpen}
            onToggleTemplates={() => setTemplatesPanelOpen(!templatesPanelOpen)}
            showIssues={SHOW_ISSUES_PANEL ? issuesPanelOpen : undefined}
            onToggleIssues={SHOW_ISSUES_PANEL ? () => setIssuesPanelOpen(!issuesPanelOpen) : undefined}
            issueCount={issues.length}
          />

          {/* Main content area — fixed sidebar widths, center takes all remaining space */}
          <main className="grid flex-1 overflow-hidden min-w-0" style={{ gridTemplateColumns: '20rem 1fr' }}>
          {/* Left panel: Dictation */}
          <div className="bg-white dark:bg-surface-900 border-r border-gray-200 dark:border-surface-800 overflow-y-auto overflow-x-hidden">
            <div className="p-4 min-w-0">
              <DictationPanel />
            </div>
          </div>

          {/* Center: Editor area — always takes remaining space */}
          <div className="min-w-0 flex flex-col overflow-hidden">
            {/* Top bar: view tabs + action buttons */}
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 bg-white dark:bg-surface-900 border-b border-gray-200 dark:border-surface-800">
              {/* Page-level view tabs use the standard pill recipe (teal
                  active state) — one tab language across the app. With dev
                  views off there is only the editor, so no tabs render. */}
              <div className="flex items-center gap-1">
                {(SHOW_DEV_VIEWS ? [
                  { view: 'editor' as const, label: 'Editor', icon: <FileText size={14} />, onClick: () => { setEditorTab('write'); setCenterView('editor'); } },
                  { view: 'debug' as const, label: 'Debug', icon: <Bug size={14} />, onClick: () => setCenterView('debug') },
                  { view: 'compare' as const, label: 'Compare', icon: <Columns size={14} />, onClick: () => setCenterView('compare') },
                ] : []).map((t) => (
                  <button
                    key={t.view}
                    onClick={t.onClick}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      centerView === t.view
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                    }`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>
              <ExportPanel />
            </div>

            {/* Editor/Debug/Compare content */}
            <div className="flex-1 overflow-hidden flex flex-col border border-gray-200 dark:border-primary-800/20 m-2 rounded-lg bg-white dark:bg-surface-950">
              {centerView === 'debug' && (
                <div className="flex-1 overflow-auto p-4">
                  <DebugView />
                </div>
              )}
              {centerView === 'compare' && (
                <div className="flex-1 overflow-auto p-4">
                  <ModelComparison />
                </div>
              )}
              {centerView === 'editor' && (
                <div className="flex-1 overflow-hidden p-4">
                  <ReportEditor />
                </div>
              )}
            </div>
          </div>

          </main>

          {/* Issues pane sits closest to the editor; templates stay outermost.
              Read-only companion — clicking an issue jumps the editor to the
              spot but never alters the report content. */}
          {SHOW_ISSUES_PANEL && issuesPanelOpen && (
            <aside className="w-72 bg-white dark:bg-surface-900 border-l border-gray-200 dark:border-surface-800 flex flex-col shrink-0">
              <IssueSidebar onClose={() => setIssuesPanelOpen(false)} />
            </aside>
          )}

          {templatesPanelOpen && <TemplateRail onClose={() => setTemplatesPanelOpen(false)} />}
        </div>

        <FeedbackButton />
      </div>
    </div>
  );
}
